// What a car's TUNE MFD gets from the library: TUNE's (from its catalogue, and for the owner's groups), the owner's own,
// and what's shared with them. Any can be taken off this car, after asking, and put back; TUNE's catalogue adds more,
// a switch each.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { components } from '@tune/shared';
import { data, problem } from '../../api';
import { useAuth } from '../../auth';
import { kindWord, size, sourceWords } from '../../items';
import { keys } from '../../queries';
import {
  Banner,
  Button,
  EmptyState,
  ErrorBanner,
  ListControl,
  ListSection,
  ListValue,
  SavingSwitch,
  Sheet,
  Skeleton,
  useConfirm,
  useRouteSheet,
  useToast,
} from '../../ui';
import { BooksIcon, PlusIcon } from '../../ui/icons';
import { CarPage } from './CarPage';
import s from './Sections.module.css';

type Vehicle = components['schemas']['Vehicle'];
type Item = components['schemas']['LibraryItem'];

export function CarLibrary() {
  const [adding, setAdding] = useRouteSheet('catalogue');
  return (
    <CarPage
      section="Library"
      headActions={
        <Button size="small" icon={<PlusIcon size={16} weight="bold" />} onClick={() => setAdding(true)}>
          Add from TUNE
        </Button>
      }
    >
      {(car) => <LibraryOf car={car} adding={adding} onAdd={() => setAdding(true)} onAdded={() => setAdding(false)} />}
    </CarPage>
  );
}

/** What an item is, in a line: "Dashboard · Version 1.2.0 · 840 KB". */
function itemLine(item: Pick<Item, 'kind' | 'version' | 'size'>): string {
  return [kindWord(item.kind), item.version ? `Version ${item.version}` : null, size(item.size)]
    .filter(Boolean)
    .join(' · ');
}

function LibraryOf({
  car,
  adding,
  onAdd,
  onAdded,
}: {
  car: Vehicle;
  adding: boolean;
  onAdd: () => void;
  onAdded: () => void;
}) {
  const api = useAuth().api('owners');
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const say = useToast();
  const path = { params: { path: { vehicleId: car.id } } };
  const library = useQuery({
    queryKey: keys.vehicleLibrary(car.id),
    queryFn: () => data(api.GET('/vehicles/{vehicleId}/library', path)),
  });
  const changed = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: keys.vehicleLibrary(car.id) }),
      queryClient.invalidateQueries({ queryKey: keys.catalogue }),
    ]);
  };
  const takeOff = useMutation({
    mutationFn: (item: Item) =>
      data(
        api.DELETE('/vehicles/{vehicleId}/library/{itemId}', {
          params: { path: { vehicleId: car.id, itemId: item.id } },
        }),
      ),
    onSuccess: async (_, item) => {
      await changed();
      say(`${item.name} is off ${car.nickname}.`);
    },
  });
  const putBack = useMutation({
    mutationFn: (itemId: string) =>
      data(api.PUT('/vehicles/{vehicleId}/library/{itemId}', { params: { path: { vehicleId: car.id, itemId } } })),
    onSuccess: changed,
  });
  if (library.isPending) {
    return <Skeleton blocks={2} lines={0} label="Loading what the MFD gets" />;
  }
  if (library.isError) {
    return <ErrorBanner retry={() => void library.refetch()}>The car's library couldn't load.</ErrorBanner>;
  }
  const ask = async (item: Item) => {
    const tune = item.source.kind === 'tune';
    const yes = await confirm({
      title: `Take ${item.name} off ${car.nickname}?`,
      message: `Its MFD removes it at its next sync, never while it's on screen or in use.${
        tune ? " It's still in TUNE's catalogue, to add again." : ' It stays in your library, to put back.'
      }`,
      confirm: 'Take off',
    });
    if (yes) {
      takeOff.mutate(item);
    }
  };
  const { items, removed } = library.data;
  const groups: Array<[string, Item[]]> = [
    ['From TUNE', items.filter((i) => i.source.kind === 'tune' || i.source.kind === 'group')],
    ['Yours', items.filter((i) => i.source.kind === 'own')],
    ['Shared with you', items.filter((i) => i.source.kind === 'shared')],
  ];
  const failed = takeOff.error ?? putBack.error;
  return (
    <>
      <p className={s.lead}>
        What this car's TUNE MFD gets. It installs each by itself, and TUNE's newer versions too, unless the copy on the
        MFD was changed there.
      </p>
      {failed && <Banner tone="error">{problem(failed)}</Banner>}
      {items.length === 0 && (
        <EmptyState
          icon={<BooksIcon size={56} />}
          title="Nothing on this car yet"
          action={<Button onClick={onAdd}>Add from TUNE's catalogue</Button>}
        >
          Dashboards and configs from TUNE, your own, and what's shared with you come to its MFD from here.
        </EmptyState>
      )}
      <div className={s.grid}>
        {groups
          .filter(([, these]) => these.length > 0)
          .map(([heading, these]) => (
            <ListSection key={heading} header={heading}>
              {these.map((item) => (
                <ListValue
                  key={item.id}
                  label={item.name}
                  subtitle={
                    item.source.kind === 'tune' || item.source.kind === 'own'
                      ? itemLine(item)
                      : `${sourceWords(item.source)} · ${itemLine(item)}`
                  }
                  detail={
                    <Button variant="plain" size="small" disabled={takeOff.isPending} onClick={() => void ask(item)}>
                      Take off<span className="visually-hidden"> {item.name}</span>
                    </Button>
                  }
                />
              ))}
            </ListSection>
          ))}
        {removed.length > 0 && (
          <ListSection header="Taken off this car" footer="Its MFD doesn't get these. Put one back to send it again.">
            {removed.map((item) => (
              <ListValue
                key={item.id}
                label={item.name}
                subtitle={kindWord(item.kind)}
                detail={
                  <Button
                    variant="plain"
                    size="small"
                    disabled={putBack.isPending}
                    onClick={() => putBack.mutate(item.id)}
                  >
                    Put back<span className="visually-hidden"> {item.name}</span>
                  </Button>
                }
              />
            ))}
          </ListSection>
        )}
      </div>
      <CatalogueSheet car={car} open={adding} onClose={onAdded} />
    </>
  );
}

/** TUNE's catalogue, with a switch each for whether this car has it. Each saves as it's flipped. */
function CatalogueSheet({ car, open, onClose }: { car: Vehicle; open: boolean; onClose: () => void }) {
  const api = useAuth().api('owners');
  const queryClient = useQueryClient();
  const catalogue = useQuery({
    queryKey: keys.catalogue,
    queryFn: () => data(api.GET('/catalogue')),
    enabled: open,
  });
  const set = async (itemId: string, on: boolean) => {
    const path = { params: { path: { vehicleId: car.id, itemId } } };
    await data(
      on
        ? api.PUT('/vehicles/{vehicleId}/library/{itemId}', path)
        : api.DELETE('/vehicles/{vehicleId}/library/{itemId}', path),
    );
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: keys.catalogue }),
      queryClient.invalidateQueries({ queryKey: keys.vehicleLibrary(car.id) }),
    ]);
  };
  const entries = catalogue.data?.items ?? [];
  return (
    <Sheet open={open} onClose={onClose} title="TUNE's catalogue" cancelLabel="Done" size="large">
      <p className={s.note}>
        Switched on, a dashboard or config goes to {car.nickname}'s MFD at its next sync, with TUNE's newer versions as
        they come.
      </p>
      {catalogue.isPending && <Skeleton lines={4} label="Loading TUNE's catalogue" />}
      {catalogue.isError && (
        <ErrorBanner retry={() => void catalogue.refetch()}>TUNE's catalogue couldn't load.</ErrorBanner>
      )}
      {catalogue.isSuccess && entries.length === 0 && <p className={s.note}>TUNE hasn't listed anything yet.</p>}
      {(['dashboard', 'config'] as const).map((kind) => {
        const these = entries.filter((e) => e.kind === kind);
        return (
          these.length > 0 && (
            <ListSection key={kind} header={kind === 'dashboard' ? 'Dashboards' : 'Configs'} level={3}>
              {these.map((entry) => (
                <ListControl
                  key={entry.id}
                  label={entry.name}
                  subtitle={[entry.version ? `Version ${entry.version}` : null, entry.author, size(entry.size)]
                    .filter(Boolean)
                    .join(' · ')}
                  control={<SavingSwitch checked={entry.vehicles.includes(car.id)} save={(on) => set(entry.id, on)} />}
                />
              ))}
            </ListSection>
          )
        );
      })}
    </Sheet>
  );
}
